/* ClarityCard — 3D animated career-identity card.
   Cursor tilt + light sweep + real 3D flip on tap + entry rise animation.
   All motion is pure CSS transforms (GPU-friendly on low-end mobiles). */
import React, { useRef, useState, useEffect } from 'react';
import { Sparkles, Award, BookOpen, Wrench, ExternalLink, RotateCcw } from 'lucide-react';

const DEEP = '#0B1F2E';
const PARCHMENT = '#F5E9D4';
const TEAL = '#2DD4BF';

const CardStyles = () => (
  <style>{`
    @keyframes cc-enter {
      0% { opacity: 0; transform: translateY(46px) rotateX(18deg) rotateY(-14deg) scale(.94); }
      60% { opacity: 1; }
      100% { opacity: 1; transform: translateY(0) rotateX(0) rotateY(0) scale(1); }
    }
    @keyframes cc-float {
      0%, 100% { transform: translateY(0); }
      50% { transform: translateY(-7px); }
    }
    .cc-scene { perspective: 1400px; }
    .cc-tilt { transform-style: preserve-3d; transition: transform .18s ease-out; animation: cc-enter .9s cubic-bezier(.22,1,.36,1) both; }
    .cc-tilt.cc-idle { animation: cc-enter .9s cubic-bezier(.22,1,.36,1) both, cc-float 5s ease-in-out 1s infinite; }
    .cc-inner { position: relative; width: 100%; height: 100%; transform-style: preserve-3d; transition: transform .7s cubic-bezier(.4,.1,.2,1); }
    .cc-flipped .cc-inner { transform: rotateY(180deg); }
    .cc-face { position: absolute; inset: 0; backface-visibility: hidden; -webkit-backface-visibility: hidden; border-radius: 22px; overflow: hidden; }
    .cc-back-face { transform: rotateY(180deg); }
    .cc-sheen { position: absolute; inset: 0; pointer-events: none; border-radius: 22px;
      background: radial-gradient(420px circle at var(--mx,50%) var(--my,20%), rgba(245,233,212,.14), transparent 55%); }
    .cc-chip { display: inline-flex; align-items: center; padding: 3px 10px; border-radius: 999px; font-size: 10px; font-weight: 700; letter-spacing: .06em; text-transform: uppercase; }
  `}</style>
);

const Ring = ({ pct, size = 54 }) => {
  const r = (size - 8) / 2;
  const c = 2 * Math.PI * r;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} data-testid="clarity-card-progress-ring">
      <circle cx={size / 2} cy={size / 2} r={r} stroke={`${PARCHMENT}4D`} strokeWidth="5" fill="none" />
      <circle cx={size / 2} cy={size / 2} r={r} stroke={TEAL} strokeWidth="5" fill="none"
        strokeLinecap="round" strokeDasharray={c} strokeDashoffset={c * (1 - (pct || 0) / 100)}
        transform={`rotate(-90 ${size / 2} ${size / 2})`} style={{ transition: 'stroke-dashoffset 1s ease' }} />
      <text x="50%" y="54%" textAnchor="middle" dominantBaseline="middle" fill={PARCHMENT} fontSize="12" fontWeight="800">
        {pct || 0}%
      </text>
    </svg>
  );
};

const Avatar = ({ card, size = 56 }) => {
  const src = card.avatar === 'male' ? '/avatars/male.png' : card.avatar === 'female' ? '/avatars/female.png' : null;
  if (src) {
    return <img src={src} alt="" className="rounded-full object-cover border-2" style={{ width: size, height: size, borderColor: `${PARCHMENT}55` }} />;
  }
  return (
    <div className="rounded-full flex items-center justify-center font-black"
      style={{ width: size, height: size, background: `linear-gradient(135deg, ${TEAL}, #0891B2)`, color: '#fff', fontSize: size * 0.4 }}>
      {(card.name || 'S')[0].toUpperCase()}
    </div>
  );
};

const CountBox = ({ value, label, testId }) => {
  const long = String(value).length > 6;
  return (
    <div className="flex-1 rounded-xl py-2.5 px-1 text-center flex flex-col justify-center" style={{ background: `${PARCHMENT}0F`, border: `1px solid ${PARCHMENT}1A` }} data-testid={testId}>
      <div className={long ? 'text-[11.5px] font-black leading-tight px-0.5' : 'text-xl font-black'} style={{ color: PARCHMENT }}>{value}</div>
      <div className="text-[9px] font-bold uppercase tracking-widest mt-0.5" style={{ color: `${PARCHMENT}77` }}>{label}</div>
    </div>
  );
};

export const GhostCard = () => (
  <div className="cc-scene mx-auto" style={{ width: 'min(380px, 92vw)' }}>
    <CardStyles />
    <div className="cc-tilt rounded-[22px] p-8 text-center flex flex-col items-center justify-center"
      style={{ minHeight: 420, border: `2px dashed ${DEEP}33`, background: '#F8FAFC' }}
      data-testid="clarity-card-ghost">
      <Sparkles className="w-8 h-8 mb-4" style={{ color: `${DEEP}44` }} />
      <p className="font-bold text-lg mb-2" style={{ color: `${DEEP}99` }}>Your Career Clarity Card is waiting</p>
      <p className="text-sm" style={{ color: `${DEEP}66` }}>Take your first quiz to bring it to life.</p>
    </div>
  </div>
);

export default function ClarityCard({ card, publicView = false }) {
  const [flipped, setFlipped] = useState(false);
  const [tilting, setTilting] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => { setFlipped(false); }, [card?.slug]);

  if (!card) return null;
  if (card.is_empty && !publicView) return <GhostCard />;

  const onMove = (e) => {
    const el = wrapRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width;
    const py = (e.clientY - rect.top) / rect.height;
    el.style.setProperty('--mx', `${px * 100}%`);
    el.style.setProperty('--my', `${py * 100}%`);
    el.style.transform = `rotateY(${(px - 0.5) * 14}deg) rotateX(${(0.5 - py) * 12}deg)`;
    if (!tilting) setTilting(true);
  };
  const onLeave = () => {
    const el = wrapRef.current;
    if (el) el.style.transform = '';
    setTilting(false);
  };

  const fit = card.fit;
  const fitLine = fit ? [fit.title, fit.band].filter(Boolean).join(' · ') : null;
  const counts = card.counts || {};
  const tiles = (card.tiles && card.tiles.length === 3) ? card.tiles : [
    { label: 'Stories lived', value: counts.stories_lived ?? 0 },
    { label: 'Tests taken', value: counts.quizzes_taken ?? 0 },
    { label: 'Skills started', value: counts.skills_started ?? 0 },
  ];

  return (
    <div className="cc-scene mx-auto select-none" style={{ width: 'min(380px, 92vw)' }}>
      <CardStyles />
      <div
        ref={wrapRef}
        className={`cc-tilt ${tilting ? '' : 'cc-idle'} ${flipped ? 'cc-flipped' : ''} cursor-pointer`}
        style={{ height: 520 }}
        onMouseMove={onMove}
        onMouseLeave={onLeave}
        onClick={() => setFlipped((f) => !f)}
        data-testid="clarity-card"
      >
        <div className="cc-inner">

          {/* ───────── FRONT ───────── */}
          <div className="cc-face flex flex-col p-6" style={{ background: `linear-gradient(160deg, ${DEEP} 0%, #10293C 55%, #0D2233 100%)`, border: `1px solid ${PARCHMENT}26`, boxShadow: '0 24px 60px -18px rgba(11,31,46,.55)' }} data-testid="clarity-card-front">
            <div className="cc-sheen" />
            <div className="flex items-start justify-between mb-5">
              <div>
                <p className="text-[10px] font-black tracking-[0.28em]" style={{ color: TEAL }}>LEARNIXTA</p>
                <p className="text-[10px] font-bold tracking-[0.18em] uppercase" style={{ color: `${PARCHMENT}66` }}>Career Clarity Card</p>
              </div>
              <Ring pct={card.progress_pct} />
            </div>

            <div className="flex items-center gap-3 mb-5">
              <Avatar card={card} />
              <div className="min-w-0">
                <p className="font-black text-lg leading-tight truncate" style={{ color: PARCHMENT }} data-testid="clarity-card-name">{card.name}</p>
                {card.member_since && (
                  <p className="text-[10px]" style={{ color: `${PARCHMENT}66` }}>Member since {String(card.member_since).slice(0, 10)}</p>
                )}
              </div>
            </div>

            <div className="mb-4">
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] mb-1" style={{ color: `${PARCHMENT}66` }}>Committed path</p>
              <p className="font-black leading-tight" style={{ color: PARCHMENT, fontSize: 26 }} data-testid="clarity-card-path">
                {card.committed_path || 'Still exploring'}
              </p>
            </div>

            {fitLine && (
              <div className="mb-5">
                <p className="text-[10px] font-bold uppercase tracking-[0.2em] mb-1" style={{ color: `${PARCHMENT}66` }}>Fit summary</p>
                <p className="text-sm font-semibold" style={{ color: TEAL }} data-testid="clarity-card-fit">{fitLine}</p>
              </div>
            )}

            <div className="flex gap-2 mt-auto mb-4" data-testid="clarity-card-tiles">
              {tiles.map((t, i) => (
                <CountBox key={t.label} value={t.value} label={t.label} testId={`clarity-card-tile-${i}`} />
              ))}
            </div>

            <p className="text-center text-[10px] font-bold uppercase tracking-[0.22em] flex items-center justify-center gap-1.5" style={{ color: `${PARCHMENT}55` }}>
              <RotateCcw size={11} /> Tap to flip
            </p>
          </div>

          {/* ───────── BACK ───────── */}
          <div className="cc-face cc-back-face flex flex-col p-6 overflow-y-auto" style={{ background: `linear-gradient(200deg, #10293C 0%, ${DEEP} 60%)`, border: `1px solid ${PARCHMENT}26`, boxShadow: '0 24px 60px -18px rgba(11,31,46,.55)' }} data-testid="clarity-card-back">
            <div className="cc-sheen" />
            <div className="flex items-center justify-between mb-4">
              <p className="text-[10px] font-black tracking-[0.28em]" style={{ color: TEAL }}>{card.name?.toUpperCase()}</p>
              <Ring pct={card.progress_pct} size={44} />
            </div>

            {!publicView && (card.strengths || []).length > 0 && (
              <div className="mb-4" data-testid="clarity-card-strengths">
                <p className="text-[10px] font-bold uppercase tracking-[0.2em] mb-1.5 flex items-center gap-1" style={{ color: `${PARCHMENT}66` }}><Sparkles size={10} /> Strengths showing up</p>
                <div className="flex flex-wrap gap-1.5">
                  {card.strengths.map((s) => (
                    <span key={s} className="cc-chip" style={{ background: `${TEAL}1F`, color: TEAL }}>{s}</span>
                  ))}
                </div>
              </div>
            )}

            {!publicView && (card.stories?.yes?.length || card.stories?.maybe?.length || card.stories?.no?.length) ? (
              <div className="mb-4" data-testid="clarity-card-stories">
                <p className="text-[10px] font-bold uppercase tracking-[0.2em] mb-1.5 flex items-center gap-1" style={{ color: `${PARCHMENT}66` }}><BookOpen size={10} /> Days I lived (Oka Roju)</p>
                {['yes', 'maybe', 'no'].map((b) => (card.stories?.[b] || []).map((n) => (
                  <div key={b + n} className="flex items-center gap-2 text-xs py-0.5" style={{ color: `${PARCHMENT}CC` }}>
                    <span className="font-black" style={{ color: b === 'yes' ? '#4ADE80' : b === 'maybe' ? '#FBBF24' : '#F87171', width: 14 }}>
                      {b === 'yes' ? '✓' : b === 'maybe' ? '~' : '✗'}
                    </span>
                    <span className="truncate">{n}</span>
                  </div>
                )))}
              </div>
            ) : null}

            {(card.badges || []).length > 0 && (
              <div className="mb-4" data-testid="clarity-card-badges">
                <p className="text-[10px] font-bold uppercase tracking-[0.2em] mb-1.5 flex items-center gap-1" style={{ color: `${PARCHMENT}66` }}><Award size={10} /> Verified build badges</p>
                {card.badges.map((b) => (
                  <div key={b.title} className="flex items-center justify-between gap-2 text-xs py-1" style={{ color: PARCHMENT }}>
                    <span className="truncate font-semibold">{b.title}</span>
                    {b.verify_url && (
                      <a href={b.verify_url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}
                        className="inline-flex items-center gap-1 shrink-0 font-bold" style={{ color: TEAL }} data-testid="clarity-card-badge-verify">
                        Verify <ExternalLink size={10} />
                      </a>
                    )}
                  </div>
                ))}
              </div>
            )}

            {!publicView && (card.life_skills || []).length > 0 && (
              <div className="mb-4" data-testid="clarity-card-lifeskills">
                <p className="text-[10px] font-bold uppercase tracking-[0.2em] mb-1.5 flex items-center gap-1" style={{ color: `${PARCHMENT}66` }}><Wrench size={10} /> Backup strength · life skills</p>
                <div className="flex flex-wrap gap-1.5">
                  {card.life_skills.map((s) => (
                    <span key={s} className="cc-chip" style={{ background: `${PARCHMENT}14`, color: `${PARCHMENT}CC` }}>{s}</span>
                  ))}
                </div>
              </div>
            )}

            {publicView && (card.badges || []).length === 0 && (
              <p className="text-xs text-center py-8" style={{ color: `${PARCHMENT}55` }}>
                Verified build badges will appear here as they are earned.
              </p>
            )}

            <p className="mt-auto pt-2 text-center text-[10px] font-bold uppercase tracking-[0.22em]" style={{ color: `${PARCHMENT}44` }}>
              {publicView ? `${card.progress_pct || 0}% of paths explored` : `${card.paths_touched?.length || 0}/10 paths explored`} · tap to flip back
            </p>
          </div>

        </div>
      </div>
    </div>
  );
}
